import { useState } from 'react'
import { X, Send, Loader2, AlertTriangle } from 'lucide-react'
import { api } from '../lib/api'

// Admin-triggered receipt re-send modal. Used from the Orders drawer
// (delivery) and PosSales drawer (POS). Talks to
// POST /admin/orders/:id/send-receipt (super_admin | operations |
// accounting).
//
// The endpoint enforces a 5-per-order-per-hour rate limit shared across
// all origins (cashier, customer-app, admin), so an operator hammering
// Send won't spam Twilio / Resend.

interface Props {
  orderId: string
  orderNumber: string
  onClose: () => void
}

export function SendReceiptModal({ orderId, orderNumber, onClose }: Props) {
  const [channel, setChannel] = useState<'sms' | 'email'>('sms')
  const [to, setTo] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)

  async function submit() {
    setError(null)
    const trimmed = to.trim()
    if (!trimmed) { setError('Enter a phone number or email address'); return }
    setBusy(true)
    try {
      await api.post(`/orders/${orderId}/send-receipt`, {
        channel,
        to: trimmed,
      })
      setSent(true)
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string; code?: string } } }
      setError(err.response?.data?.message ?? 'Failed to send receipt')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md bg-[#0f0f0f] border border-white/10 rounded-xl p-6 space-y-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-white text-base font-semibold">Send receipt</h3>
            <p className="text-xs text-gray-500 mt-0.5 font-mono">{orderNumber}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={20} /></button>
        </div>

        {!sent && (
          <>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setChannel('sms'); setTo(''); setError(null) }}
                disabled={busy}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  channel === 'sms'
                    ? 'bg-[#c41e3a] text-white'
                    : 'bg-[#242424] border border-[#3a3a3a] text-gray-300 hover:bg-[#2a2a2a]'
                }`}
              >
                SMS
              </button>
              <button
                type="button"
                onClick={() => { setChannel('email'); setTo(''); setError(null) }}
                disabled={busy}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  channel === 'email'
                    ? 'bg-[#c41e3a] text-white'
                    : 'bg-[#242424] border border-[#3a3a3a] text-gray-300 hover:bg-[#2a2a2a]'
                }`}
              >
                Email
              </button>
            </div>

            <div>
              <label className="text-xs text-gray-500 block mb-1">
                {channel === 'sms' ? 'Jordan mobile (+962 77 / 78 / 79)' : 'Email address'}
              </label>
              <input
                type={channel === 'email' ? 'email' : 'tel'}
                value={to}
                onChange={e => setTo(e.target.value)}
                disabled={busy}
                autoFocus
                placeholder={channel === 'sms' ? '+962 77 123 4567' : 'customer@example.com'}
                className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c41e3a]"
              />
              <p className="text-[10px] text-gray-600 mt-1.5 leading-relaxed">
                Sends a link valid for 90 days. Rate-limited to 5 sends per order per hour (shared with the cashier + customer-app send origins).
              </p>
            </div>

            {error && (
              <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300 flex items-start gap-2">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
          </>
        )}

        {sent && (
          <div className="rounded-lg border border-green-500/40 bg-green-500/10 px-4 py-3">
            <div className="text-sm text-green-300 font-medium">
              {channel === 'sms' ? 'SMS sent' : 'Email sent'}
            </div>
            <div className="text-xs text-green-300/80 mt-0.5">
              Delivered to <span className="font-mono">{to}</span>. Link expires in 90 days.
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-1">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-400 hover:text-white"
          >
            {sent ? 'Close' : 'Cancel'}
          </button>
          {!sent && (
            <button
              onClick={submit}
              disabled={busy}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {busy ? 'Sending…' : 'Send'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
